import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Stack,
  Button,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Skeleton,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Search,
  FilterList,
  Business,
  Email,
  Phone,
  LocationOn,
  MoreVert,
  Refresh,
  Sort,
  ArrowUpward,
  ArrowDownward,
  Groups,
  CheckCircle,
  Cancel,
  People,
  Support,
  Chat,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { socketService } from '../services/socket';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useLayout } from '../contexts/LayoutContext';

interface Client {
  id: string;
  client_name: string;
  email: string;
  telephone: string;
  address: string;
  is_active: boolean;
  is_deleted: boolean;
  created: Date;
  updated: Date;
  // Additional computed fields
  userCount?: number;
  activeUsers?: number;
  openTickets?: number;
}

type SortField = 'name' | 'created' | 'updated';
type SortOrder = 'asc' | 'desc';

const Clients: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { preferences } = useLayout();
  const isSidebarLayout = preferences.layoutMode === 'sidebar';

  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  // Fetch clients from backend
  const fetchClients = useCallback(() => {
    const socket = socketService.getSocket();
    if (!socket) {
      console.error('No socket connection');
      setLoading(false);
      return;
    }

    setLoading(true);
    socket.emit('getClients', {
      search: searchTerm,
      limit,
      offset: page * limit,
      sortBy: sortField,
      sortOrder,
    }, (response: any) => {
      if (response.success && response.data) {
        setClients(response.data.clients || []);
        setTotalCount(response.data.total || 0);
      } else {
        console.error('Failed to fetch clients:', response.error);
        setClients([]);
      }
      setLoading(false);
    });
  }, [searchTerm, page, limit, sortField, sortOrder]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter clients locally (in addition to server-side search)
  useEffect(() => {
    if (!searchTerm) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.telephone.includes(searchTerm)
      );
      setFilteredClients(filtered);
    }
  }, [clients, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, client: Client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleViewUsers = (clientId: string) => {
    navigate(`/users?clientId=${clientId}`);
    handleMenuClose();
  };

  const handleViewTickets = (clientId: string) => {
    navigate(`/tickets?clientId=${clientId}`);
    handleMenuClose();
  };

  const handleStartChat = (clientId: string) => {
    navigate(`/chat?clientId=${clientId}`);
    handleMenuClose();
  };

  const ClientCard = ({ client, index }: { client: Client; index: number }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <GlassCard gradient>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  width: 48,
                  height: 48,
                }}
              >
                <Business />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                  {client.client_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {client.is_active ? (
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 14 }} />}
                      label="Active"
                      size="small"
                      color="success"
                      sx={{ height: 20 }}
                    />
                  ) : (
                    <Chip
                      icon={<Cancel sx={{ fontSize: 14 }} />}
                      label="Inactive"
                      size="small"
                      color="default"
                      sx={{ height: 20 }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    ID: {client.id.substring(0, 8)}...
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, client)}
            >
              <MoreVert />
            </IconButton>
          </Box>

          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                {client.email}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                {client.telephone}
              </Typography>
            </Box>
            {client.address && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOn sx={{ fontSize: 16, color: theme.palette.text.secondary, mt: 0.25 }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {client.address}
                </Typography>
              </Box>
            )}
          </Stack>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                {client.userCount || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Users
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={600} color="success.main">
                {client.activeUsers || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Online
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={600} color="warning.main">
                {client.openTickets || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tickets
              </Typography>
            </Box>
          </Box>
        </CardContent>
        <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            size="small"
            startIcon={<People />}
            onClick={() => handleViewUsers(client.id)}
            sx={{ flex: 1 }}
          >
            Users
          </Button>
          <Button
            size="small"
            startIcon={<Support />}
            onClick={() => handleViewTickets(client.id)}
            sx={{ flex: 1 }}
          >
            Tickets
          </Button>
          <Button
            size="small"
            startIcon={<Chat />}
            onClick={() => handleStartChat(client.id)}
            sx={{ flex: 1 }}
            variant="contained"
          >
            Chat
          </Button>
        </CardActions>
      </GlassCard>
    </motion.div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        mt: -3,
        pt: 3,
        ...(isSidebarLayout && {
          WebkitAppRegion: 'drag',
          '-webkit-app-region': 'drag',
        })
      }}>
        <Typography 
          variant="h4" 
          fontWeight={700} 
          gutterBottom
          sx={{
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Clients
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          {totalCount > 0 ? `${totalCount} total clients` : 'Manage your client accounts'}
        </Typography>
      </Box>

      {/* Search and Filters */}
      <GlassCard sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search clients..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
              onClick={() => handleSort(sortField)}
              variant="outlined"
              size="small"
            >
              Sort by {sortField}
            </Button>
            <IconButton onClick={fetchClients} size="small">
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </GlassCard>

      {/* Clients Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))}
        </Grid>
      ) : filteredClients.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Business sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No clients found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Clients will appear here once added'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <AnimatePresence mode="popLayout">
            {filteredClients.map((client, index) => (
              <Grid key={client.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <ClientCard client={client} index={index} />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedClient && handleViewUsers(selectedClient.id)}>
          <People sx={{ mr: 1, fontSize: 20 }} />
          View Users
        </MenuItem>
        <MenuItem onClick={() => selectedClient && handleViewTickets(selectedClient.id)}>
          <Support sx={{ mr: 1, fontSize: 20 }} />
          View Tickets
        </MenuItem>
        <MenuItem onClick={() => selectedClient && handleStartChat(selectedClient.id)}>
          <Chat sx={{ mr: 1, fontSize: 20 }} />
          Start Chat
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default Clients;